import React from "react";
import useExpandableRows from "../useExpandableRows";
import { keywordTerm } from "./useKeywordClusters";
import { formatCpc, formatMetric, formatNumber } from "./clusterFormat";

/**
 * The live contents of blog_keyword_clusters.
 *
 * Collapsed by default: thirteen clusters × ~7 keywords is ~90 rows, which
 * buries the cluster-level signal (which topic has demand, where we rank) that
 * this screen exists to give. Expanding a cluster reveals its human-authored
 * angle alongside the per-keyword metrics that produced its totals.
 */

/** Keyword-level detail for one expanded cluster. */
const KeywordRows = ({ cluster }) => (
  <div className="px-5 py-4 bg-gray-50/70">
    {cluster.angle && (
      <p className="text-xs text-gray-600 mb-3">
        <span className="font-semibold text-gray-700">Angle:</span> {cluster.angle}
      </p>
    )}

    {!cluster.keywords.length ? (
      <p className="text-xs text-gray-500">This cluster has no keywords.</p>
    ) : (
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
            <th className="py-1.5 pr-4 font-medium">Keyword</th>
            <th className="py-1.5 px-2 font-medium text-right">Volume</th>
            <th className="py-1.5 px-2 font-medium text-right">KD</th>
            <th className="py-1.5 px-2 font-medium text-right">CPC</th>
            <th className="py-1.5 pl-2 font-medium text-right">Position</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {cluster.keywords.map((kw, i) => {
            const term = keywordTerm(kw);
            const ranked = kw?.position !== null && kw?.position !== undefined;
            return (
              <tr key={`${cluster.id}-${term || i}`}>
                <td className="py-1.5 pr-4 text-gray-800">
                  {term || <span className="text-gray-400">(unnamed)</span>}
                  {kw?.note && (
                    <span className="block text-[11px] text-gray-500 mt-0.5">— {kw.note}</span>
                  )}
                </td>
                <td className="py-1.5 px-2 text-right tabular-nums text-gray-700">
                  {formatMetric(kw?.volume)}
                </td>
                <td className="py-1.5 px-2 text-right tabular-nums text-gray-600">
                  {formatMetric(kw?.kd)}
                </td>
                <td className="py-1.5 px-2 text-right tabular-nums text-gray-600">
                  {formatCpc(kw?.cpc)}
                </td>
                <td
                  className={`py-1.5 pl-2 text-right tabular-nums font-medium ${
                    ranked ? "text-green-700" : "text-gray-400"
                  }`}
                >
                  {formatMetric(kw?.position)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    )}

    {!!cluster.titlePatterns.length && (
      <p className="text-[11px] text-gray-500 mt-3">
        <span className="font-semibold text-gray-600">Title patterns:</span>{" "}
        {cluster.titlePatterns.map((p, i) => (
          <code key={i} className="bg-gray-200/70 rounded px-1 py-0.5 mr-1 font-mono">
            {p}
          </code>
        ))}
      </p>
    )}
  </div>
);

const ClustersTable = ({ clusters, loading, error }) => {
  const { toggleRow, isExpanded } = useExpandableRows();

  if (loading) {
    return <p className="text-sm text-gray-500 px-5 py-4">Loading clusters…</p>;
  }

  if (error) {
    return (
      <p className="text-sm text-red-600 px-5 py-4">
        <strong>Could not load clusters:</strong> {error}
      </p>
    );
  }

  if (!clusters.length) {
    return (
      <p className="text-sm text-gray-500 px-5 py-4">
        <code className="bg-gray-100 rounded px-1 py-0.5 font-mono text-xs">
          blog_keyword_clusters
        </code>{" "}
        is empty — the collection must be seeded before blog generation can run.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
            <th className="px-5 py-2 font-medium w-10" />
            <th className="px-5 py-2 font-medium">Cluster</th>
            <th className="px-5 py-2 font-medium">Intent</th>
            <th className="px-5 py-2 font-medium text-right">Keywords</th>
            <th className="px-5 py-2 font-medium text-right">Total volume</th>
            <th className="px-5 py-2 font-medium text-right">Best position</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {clusters.map((cluster) => {
            const open = isExpanded(cluster.id);
            // hasMeasuredVolume is derived by the monthly refresh, so trust it
            // when present and only fall back for never-refreshed docs.
            const measured = cluster.hasMeasuredVolume ?? (cluster.totalVolume || 0) > 0;
            const unranked =
              cluster.bestPosition === null || cluster.bestPosition === undefined;

            return (
              <React.Fragment key={cluster.id}>
                <tr
                  onClick={() => toggleRow(cluster.id)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-5 py-3 text-gray-400 select-none">{open ? "▾" : "▸"}</td>
                  <td className="px-5 py-3">
                    <span className="font-medium text-gray-900">
                      {cluster.name || cluster.id}
                    </span>
                    {!measured && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-600 rounded align-middle">
                        No demand
                      </span>
                    )}
                    {cluster.active === false && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-700 rounded align-middle">
                        Inactive
                      </span>
                    )}
                    <span className="block text-xs text-gray-500 font-normal mt-0.5">
                      #{cluster.priority ?? "—"} · {cluster.id}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{cluster.intent || "—"}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-gray-600">
                    {cluster.keywords.length}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-medium text-gray-900">
                    {formatNumber(cluster.totalVolume)}
                  </td>
                  <td
                    className={`px-5 py-3 text-right tabular-nums font-medium ${
                      unranked ? "text-gray-400" : "text-green-700"
                    }`}
                  >
                    {formatMetric(cluster.bestPosition)}
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={6} className="p-0">
                      <KeywordRows cluster={cluster} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ClustersTable;
